import {
  useGetInstallationDecisionsQuery,
  useRemoveDecisionMutation
} from 'generated/graphql';
import { DateTime } from 'luxon';
import { IItemState } from 'modules/components/layer19/InstallationItem/InstallationItem';
import React from 'react';

export interface IData {
  decisionId: string;
  title: string;
  timeDecided: DateTime | null;
  description: string;
  isDecided: boolean | null;
  dueDate: DateTime;
  itemState: IItemState;
}

export function useQuery(id: string) {
  const tuple = useGetInstallationDecisionsQuery({
    variables: {
      id
    },
    skip: id === ''
  });

  // add isUpcoming, isOverDue, isComplete, remaining to decision - client side field
  return React.useMemo(
    () => {
      const data: IData[] = tuple.data?.installation?.decisions.map((node) => ({
        title: node.title,
        decisionId: node.id,
        isDecided: node.approved ?? null,
        description: node.description,
        timeDecided: node.decidedTimestamp ?? null,
        dueDate: node.dueDate ?? DateTime.now(),
        itemState: __determineItemState(node.isComplete, node.isDueSoon, node.isOverdue)
      })) ?? [];

      return {
        ...tuple,
        data
      };
    },
    [tuple]
  );
}

function __determineItemState(isComplete: boolean, isDueSoon: boolean, isOverdue: boolean): IItemState {
  if (isComplete) {
    return 'COMPLETE';
  }

  if (isDueSoon) {
    return 'DUESOON';
  }

  if (isOverdue) {
    return 'OVERDUE';
  }

  return 'REMAINING';
}



export function useRemoveDecision() {
  const [
    fnMutate,
    tuple
  ] = useRemoveDecisionMutation({
    refetchQueries: []
  });
  const fnWrappedMutate = React.useCallback(
    async (id: string) => {
      await fnMutate({
        variables: {
          id
        }
      });
    },
    [fnMutate]
  );

  return React.useMemo(
    () => [
      fnWrappedMutate,
      {
        ...tuple,
        data: null
      }
    ] as const,
    [
      fnWrappedMutate,
      tuple
    ]
  );
}
